// Comprehensive appraisal service type definitions for Site Builder
// Organized by category for easy selection

export interface ServiceDefinition {
  id: string;
  label: string;
  description: string;
}

export interface ServiceCategory {
  label: string;
  icon: string;
  services: ServiceDefinition[];
}

export const APPRAISAL_SERVICE_CATEGORIES: Record<string, ServiceCategory> = {
  residential: {
    label: 'Residential Appraisals',
    icon: 'Home',
    services: [
      { id: 'single-family', label: 'Single-Family Homes', description: 'Detached single-family residential properties' },
      { id: 'condo', label: 'Condominiums', description: 'Condo units and townhomes' },
      { id: 'multi-family-2-4', label: 'Multi-Family (2-4 Units)', description: 'Duplexes, triplexes, and fourplexes' },
      { id: 'manufactured', label: 'Manufactured Homes', description: 'Mobile and modular homes' },
      { id: 'pud', label: 'Planned Unit Developments', description: 'PUD properties' },
      { id: 'co-op', label: 'Cooperative Units', description: 'Co-op apartments' },
    ],
  },
  lenderPurpose: {
    label: 'Lender/Financing',
    icon: 'Landmark',
    services: [
      { id: 'conventional', label: 'Conventional Loans', description: 'Standard conforming loan appraisals' },
      { id: 'fha', label: 'FHA Appraisals', description: 'Federal Housing Administration guidelines' },
      { id: 'va', label: 'VA Appraisals', description: 'Veterans Affairs loan appraisals' },
      { id: 'usda', label: 'USDA/Rural Housing', description: 'USDA rural development loans' },
      { id: 'jumbo', label: 'Jumbo/Non-Conforming', description: 'High-value property appraisals' },
      { id: 'heloc', label: 'HELOC/Home Equity', description: 'Home equity line of credit' },
      { id: 'refinance', label: 'Refinance Appraisals', description: 'Rate/term and cash-out refinance' },
      { id: 'pmi-removal', label: 'PMI Removal', description: 'Private mortgage insurance removal' },
    ],
  },
  legalEstate: {
    label: 'Legal & Estate',
    icon: 'Scale',
    services: [
      { id: 'estate-probate', label: 'Estate/Probate', description: 'Date of death valuations' },
      { id: 'divorce', label: 'Divorce/Dissolution', description: 'Equitable distribution appraisals' },
      { id: 'bankruptcy', label: 'Bankruptcy', description: 'Asset valuation for bankruptcy' },
      { id: 'trust', label: 'Trust Valuations', description: 'Trust administration appraisals' },
      { id: 'gift-tax', label: 'Gift Tax', description: 'IRS gift tax documentation' },
      { id: 'irs-audit', label: 'IRS Audit Support', description: 'Expert witness and audit defense' },
    ],
  },
  taxRelated: {
    label: 'Tax & Assessment',
    icon: 'FileText',
    services: [
      { id: 'tax-appeal', label: 'Property Tax Appeals', description: 'Assessment challenge support' },
      { id: 'charitable-donation', label: 'Charitable Donations', description: 'IRS Form 8283 appraisals' },
      { id: 'cost-segregation', label: 'Cost Segregation', description: 'Depreciation study support' },
    ],
  },
  preTransaction: {
    label: 'Pre-Transaction',
    icon: 'TrendingUp',
    services: [
      { id: 'pre-listing', label: 'Pre-Listing', description: 'Pre-sale market value opinions' },
      { id: 'pre-purchase', label: 'Pre-Purchase', description: 'Buyer due diligence appraisals' },
      { id: 'fsbo', label: 'For Sale By Owner', description: 'FSBO pricing guidance' },
    ],
  },
  commercial: {
    label: 'Commercial Appraisals',
    icon: 'Building',
    services: [
      { id: 'office', label: 'Office Buildings', description: 'Office property valuations' },
      { id: 'retail', label: 'Retail Properties', description: 'Retail and shopping center appraisals' },
      { id: 'industrial', label: 'Industrial/Warehouse', description: 'Industrial and warehouse facilities' },
      { id: 'mixed-use', label: 'Mixed-Use Properties', description: 'Combined residential/commercial buildings' },
      { id: 'multi-family-5-plus', label: 'Multi-Family (5+ Units)', description: 'Apartment buildings and complexes' },
      { id: 'vacant-land', label: 'Vacant Land', description: 'Land and development site valuations' },
      { id: 'special-purpose', label: 'Special Purpose', description: 'Churches, schools, gas stations, etc.' },
      { id: 'hospitality', label: 'Hospitality', description: 'Hotels, motels, and lodging properties' },
      { id: 'healthcare', label: 'Healthcare Facilities', description: 'Medical offices, assisted living, nursing homes' },
      { id: 'self-storage', label: 'Self-Storage Facilities', description: 'Storage unit and mini-storage properties' },
    ],
  },
  specialty: {
    label: 'Specialty Services',
    icon: 'Award',
    services: [
      { id: 'expert-witness', label: 'Expert Witness', description: 'Litigation and court testimony' },
      { id: 'review-appraisal', label: 'Appraisal Review', description: 'Technical and compliance reviews' },
      { id: 'rent-survey', label: 'Rent Surveys', description: 'Market rent analysis' },
      { id: 'retrospective', label: 'Retrospective Valuations', description: 'Historical date appraisals' },
      { id: 'insurance', label: 'Insurance Valuations', description: 'Replacement cost estimates' },
      { id: 'relocation', label: 'Relocation Appraisals', description: 'Corporate relocation services' },
    ],
  },
};

export type ServiceCategoryKey = keyof typeof APPRAISAL_SERVICE_CATEGORIES;

// Flatten all services for easy lookup
export const ALL_SERVICES: ServiceDefinition[] = Object.values(APPRAISAL_SERVICE_CATEGORIES)
  .flatMap(category => category.services);

// Get service by ID
export function getServiceById(id: string): ServiceDefinition | undefined {
  return ALL_SERVICES.find(s => s.id === id);
}

// Get category for a service ID
export function getCategoryForService(serviceId: string): string | undefined {
  for (const [key, category] of Object.entries(APPRAISAL_SERVICE_CATEGORIES)) {
    if (category.services.some(s => s.id === serviceId)) {
      return key;
    }
  }
  return undefined;
}

// Default FAQ items for new sites
export const DEFAULT_FAQ_ITEMS = [
  {
    id: '1',
    question: 'How long does an appraisal take?',
    answer: 'A typical residential appraisal takes 3-7 business days from inspection to delivery. Complex properties may take longer.',
  },
  {
    id: '2',
    question: 'What should I do to prepare for the appraisal?',
    answer: 'Ensure access to all areas of the property, have a list of recent improvements ready, and clear any clutter for photos.',
  },
  {
    id: '3',
    question: 'How much does an appraisal cost?',
    answer: 'Fees vary based on property type, complexity, and location. Contact me for a free quote specific to your needs.',
  },
  {
    id: '4',
    question: 'What is the difference between an appraisal and a home inspection?',
    answer: 'An appraisal determines market value; a home inspection evaluates physical condition. They serve different purposes.',
  },
  {
    id: '5',
    question: 'Can I be present during the inspection?',
    answer: 'Yes, you are welcome to be present. I can answer questions and note any features you want highlighted.',
  },
  {
    id: '6',
    question: 'What factors affect my property value?',
    answer: 'Location, condition, size, recent comparable sales, improvements, and market conditions all play a role in determining value.',
  },
];

// Common appraisal type options for quote/inquiry forms
export const QUOTE_APPRAISAL_TYPES = [
  'Residential Appraisal',
  'FHA/VA Appraisal',
  'Estate/Probate Appraisal',
  'Divorce Appraisal',
  'Pre-Listing Appraisal',
  'Tax Appeal',
  'Refinance',
  'Commercial Appraisal',
  'Other',
] as const;

// Default placeholder testimonials for new sites (D3).
// `placeholder: true` keeps them visible in the editor preview but hidden on
// the published site — the appraiser must edit before they appear publicly.
// Names and details are deliberately generic; this is starter content, not a
// claim about real clients.
export const DEFAULT_TESTIMONIAL_PLACEHOLDERS = [
  {
    id: 'placeholder-1',
    name: 'Example client name',
    location: 'Bergen County',
    rating: 5,
    text: "Detailed report, fast turnaround, and they actually answered the phone. The refinance went smoothly because everything was right the first time.",
    serviceType: 'Refinance Appraisal',
    placeholder: true,
  },
  {
    id: 'placeholder-2',
    name: 'Example client name',
    location: 'Hoboken',
    rating: 5,
    text: "Professional from start to finish. The estate appraisal helped us settle the property fairly between siblings — clearly explained and well documented.",
    serviceType: 'Estate Appraisal',
    placeholder: true,
  },
  {
    id: 'placeholder-3',
    name: 'Example client name',
    location: 'Princeton',
    rating: 5,
    text: "We weren't sure what to expect from a pre-listing appraisal. The walkthrough and report gave us the confidence to price our home right.",
    serviceType: 'Pre-Listing Appraisal',
    placeholder: true,
  },
];

// Default process steps for new sites
export const DEFAULT_PROCESS_STEPS = [
  {
    id: '1',
    number: 1,
    title: 'Request Quote',
    description: 'Submit your property details and appraisal needs for a free quote.',
    icon: 'FileText',
  },
  {
    id: '2',
    number: 2,
    title: 'Schedule Inspection',
    description: 'Once confirmed, we\'ll schedule a convenient time for the property inspection.',
    icon: 'Calendar',
  },
  {
    id: '3',
    number: 3,
    title: 'Property Analysis',
    description: 'I conduct a thorough inspection and research comparable sales in your area.',
    icon: 'Search',
  },
  {
    id: '4',
    number: 4,
    title: 'Report Delivery',
    description: 'Receive your professional appraisal report within the agreed timeframe.',
    icon: 'FileCheck',
  },
];
